import { selectCurrentHotelName } from 'accessors'
import ErrorIcon from 'components/ReusableComponents/BackgroundImage/ErrorIcon'
import TextInput from 'components/ReusableComponents/ManageHotel/TextInput'
import Toggle from 'components/ReusableComponents/Toggle'
import { useEngagePlusGate } from 'components/ReusableComponents/UnlockServiceDialog'
import MediaUploader from 'features/ManageCampaigns/MediaUploader'
import TextArea from 'features/ManageCampaigns/TextArea'
import { useState } from 'react'
import { useSelector } from 'react-redux'

import styles from './EditCampaign.module.scss'

const contentTabs = {
  text: 'text',
  image: 'image'
}

function getBaseTags(hotelName) {
  return [
    {
      id: '__hotelName__',
      display: 'hotelName',
      code: '@[hotelName](__hotelName__)',
      length: hotelName.length
    },
    {
      id: '__guestName__',
      display: 'firstName',
      code: '@[firstName](__guestName__)',
      length: 8
    },
    {
      id: '__guestLastName__',
      display: 'lastName',
      code: '@[lastName](__guestLastName__)',
      length: 8
    },
    {
      id: '__checkoutDate__',
      display: 'checkoutDate',
      length: 11
    }
  ]
}

const urlRegex = /^(http|https)?:\/\/\S+/i

const MainTemplate = ({
  messageTemplate,
  onTextChange,
  messageMedia,
  setMessageMedia,
  usedAllTags,
  setUsedAllTags,
  mediaTag,
  requiredTagIds,
  isMediaRequired,
  customUrl,
  setCustomUrl,
  isCustomUrlAllowed,
  requireEngagePlusForImage = true
}) => {
  const hotelName = useSelector(selectCurrentHotelName)
  const { promptEngagePlusUpgrade } = useEngagePlusGate()
  const [activeContentTab, setActiveContentTab] = useState(contentTabs.text)

  function handleContentTabClick(tab) {
    if (
      tab === contentTabs.image &&
      requireEngagePlusForImage &&
      promptEngagePlusUpgrade()
    ) {
      return
    }

    setActiveContentTab(tab)
  }

  function handleChange(text) {
    onTextChange(text)
  }

  function handleUpdateUsedTags(usedTags) {
    const isAllTagsUsed = requiredTagIds.every(tag =>
      usedTags.find(usedTag => usedTag.id === tag)
    )
    setUsedAllTags(isAllTagsUsed)
  }

  const [useCustomUrl, setUseCustomUrl] = useState(customUrl !== null)

  const isCustomUrlValid = customUrl ? !!customUrl.match(urlRegex) : false

  function handleChangeCustomUrl(e) {
    setUseCustomUrl(e.target.checked)
    if (!e.target.check) {
      setCustomUrl(null)
    }
  }

  return (
    <div className={styles.accordionContent}>
      <div className={styles.contentTabs}>
        <button
          type="button"
          onClick={() => handleContentTabClick(contentTabs.text)}
          className={
            activeContentTab === contentTabs.text ? styles.activeStep : styles.step
          }
        >
          Text
        </button>
        <button
          type="button"
          onClick={() => handleContentTabClick(contentTabs.image)}
          className={
            activeContentTab === contentTabs.image ? styles.activeStep : styles.step
          }
        >
          Image
        </button>
      </div>
      {activeContentTab === contentTabs.text && (
        <>
          <div className={styles.textAreaHolder}>
            <span className={styles.datepickerLabel}>Message text</span>
            <div className={styles.textAreaBox}>
              <TextArea
                baseTags={getBaseTags(hotelName) || []}
                previewPrefix="123"
                value={messageTemplate}
                handleUpdateUsedTags={handleUpdateUsedTags}
                handleChange={handleChange}
                isErred={!usedAllTags}
                requiredTagIds={requiredTagIds}
              />
            </div>
          </div>
          {isCustomUrlAllowed && (
            <div className={styles.customUrlSection}>
              <Toggle
                label={'Use a custom invite link'}
                checked={useCustomUrl}
                handleChange={handleChangeCustomUrl}
                className={styles.customLinkToggle}
              />
              {useCustomUrl && (
                <div>
                  <TextInput
                    className={styles.customReviewLinkInput}
                    autoFocus
                    placeholder="https://"
                    value={customUrl || ''}
                    onChange={e => setCustomUrl(e.target.value)}
                    valid={isCustomUrlValid}
                  />
                  {customUrl && !isCustomUrlValid && (
                    <p className={styles.customRevieLinkError}>
                      Enter a valid link starting with http or https
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </>
      )}
      {activeContentTab === contentTabs.image && (
        <>
          <span className={styles.datepickerLabel}>Media (optional)</span>
          {!messageMedia && isMediaRequired && (
            <div className={styles.recommendation}>
              <ErrorIcon />
              <p>
                Adding an image or video is <strong>strongly recommended</strong> to get
                the best results from drip campaigns.
              </p>
            </div>
          )}
          <MediaUploader
            hasTitle={false}
            selectedMedia={messageMedia}
            setSelectedMedia={setMessageMedia}
            removeSelectedMedia={() => setMessageMedia(null)}
            acceptedFileTypes={['image/*', 'application/pdf']}
            previewCount={4}
            mediaTag={mediaTag}
          />
        </>
      )}
    </div>
  )
}

export default MainTemplate
